import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ROOT_API } from "../../api/config";
import Footer from "../../components/footer";
import "./style.scss";

// Public marketing landing — replaces the old login-gated empty home route.
// Croatian-only on purpose (the product is Croatia-only); the in-app i18n
// switcher still governs the application screens.

const STEPS = [
  {
    n: "01",
    title: "Upiši adresu",
    text: "Ulica i kućni broj — bez broja brojila, bez OIB-a, bez papira. Adresa ne mora biti tvoja: stan koji unajmljuješ, kuća roditelja, vikendica.",
  },
  {
    n: "02",
    title: "Potvrdi e-mail",
    text: "Jedan kod, 30 sekundi. Bez aplikacije za instaliranje i bez lozinki koje ćeš zaboraviti — radi i prijava Google računom.",
  },
  {
    n: "03",
    title: "Znaš prije mraka",
    text: "Čim HEP ili vodovod najavi radove za tvoju ulicu, stiže ti obavijest s datumom, satnicom i točnim tekstom objave.",
  },
];

const COMPARE = [
  {
    who: "HEP-ova stranica „Bez struje”",
    how: "Dnevni popisi ulica za 21 distribucijsko područje. Točni — ali moraš ih sam provjeravati, svaki dan.",
  },
  {
    who: "HEP-ov zahtjev za obavijesti",
    how: "PDF obrazac koji se šalje lokalnoj Elektri, vezan uz broj brojila — samo za vlasnike priključka.",
  },
  {
    who: "nemastruje",
    how: "Bilo koja adresa, struja i voda, prijava u 30 sekundi — obavijest stiže sama. To je sve.",
    us: true,
  },
];

const AUDIENCES = [
  {
    title: "Kućanstva",
    tag: "Besplatno",
    text: "Jedna adresa, obavijesti e-mailom — za struju i vodu. Zauvijek besplatno, da te zamrzivač, jutarnji tuš i punjenje auta nikad ne iznenade.",
  },
  {
    title: "Vikendice i roditelji",
    tag: "Premium",
    text: "Više adresa na jednom računu i SMS koji stigne i kad ne čitaš e-mail. Za kuće u koje ne gledaš svaki dan.",
  },
  {
    title: "Upravitelji zgrada i tvrtke",
    tag: "B2B",
    text: "Obveza obavještavanja stanara i pogoni koji ne smiju stati: dizala, hidrofori, hladnjače, serverske sobe. Cijeli portfelj adresa, SMS i API — javi se na info@nemastruje.com.",
    accent: true,
  },
];

const FAQ = [
  {
    q: "Odakle vam podaci?",
    a: "Iz javnih objava HEP ODS-a o planiranim radovima na struji — istih popisa koje HEP po zakonu objavljuje najmanje 48 sati prije isključenja — te iz objava komunalnih vodovoda o radovima na vodi. Provjeravamo ih četiri puta dnevno: struju za svih 21 distribucijsko područje, vodu za sve više gradova.",
  },
  {
    q: "Jeste li vi HEP?",
    a: "Nismo. nemastruje je neovisna usluga i nije povezana s HEP-om. Mi javne objave samo pretvaramo u obavijest koja stigne tebi — umjesto da ti tražiš nju.",
  },
  {
    // Prices advertised publicly MUST carry the sidrena (10.09.2026.) price — NN 101/2026 (1212).
    // Keep in sync with helpers/cjenik.py SERVICES / PRICE_HISTORY and /cjenik.
    q: "Koliko košta?",
    a: "Jedna adresa s obavijestima e-mailom (struja i voda) je besplatna — zauvijek. Premium (do 5 adresa + SMS) je 24,99 € godišnje (sidrena cijena 24,99 €), Business (do 15 adresa + SMS) 62,49 € godišnje (sidrena cijena 62,49 €), PDV uključen. Sidrena cijena je cijena koja je vrijedila 10. rujna 2026.; cijeli cjenik s dodatnim cijenama je na /cjenik. Tvrtke i upravitelji s većim potrebama: javi se na info@nemastruje.com.",
  },
  {
    q: "Što s kvarovima i nenajavljenim prekidima?",
    a: "Za sada pratimo planirane radove — one za koje HEP i vodovodi unaprijed objave popis ulica. Obavijesti o kvarovima su na popisu želja, ali ne obećavamo ono što još ne radimo.",
  },
  {
    q: "Što radite s mojom adresom?",
    a: "Koristimo je isključivo za usporedbu s HEP-ovim objavama. Geokodiranje radimo na vlastitoj infrastrukturi u EU, podatke ne prodajemo i račun možeš obrisati kad god želiš. Detalji u politici privatnosti.",
  },
];

function useAuthed() {
  const [authed, setAuthed] = useState(false);
  useEffect(() => {
    setAuthed(Boolean(localStorage.getItem("access_token")));
  }, []);
  return authed;
}

function usePublicStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    axios
      .get(`${ROOT_API}apis/public-stats`, { timeout: 6000 })
      .then((res) => {
        if (alive && res?.data?.outages_upcoming !== undefined) setStats(res.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return stats;
}

// Town-search entry point. Pulls the published-town list from the SEO service
// (/grad-index.json, served by Caddy on the same origin) and sends the visitor to that town's
// /grad/<slug> landing page; falls back to the /gradovi directory if there's no match.
function CityFinder() {
  const [towns, setTowns] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetch("/grad-index.json")
      .then((r) => r.json())
      .then((d) => { if (alive && Array.isArray(d)) setTowns(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Close the dropdown when clicking outside the box.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
  const nq = norm(q.trim());
  const matches = nq
    ? towns
        .filter((t) => norm(t.name).includes(nq))
        .sort((a, b) => {
          const as = norm(a.name).startsWith(nq) ? 0 : 1;
          const bs = norm(b.name).startsWith(nq) ? 0 : 1;
          return as - bs || a.name.localeCompare(b.name, "hr");
        })
    : towns;

  const go = (t) => { window.location.href = `/grad/${t.slug}`; };

  const submit = (e) => {
    e.preventDefault();
    const t = (active >= 0 && matches[active]) || matches[0];
    window.location.href = t ? `/grad/${t.slug}` : "/gradovi";
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <section className="landing__section landing__section--tight">
      <h2 className="landing__h2">Provjeri prekide za svoj grad</h2>
      <form className="landing__finder" onSubmit={submit} autoComplete="off">
        <div className="landing__finder-box" ref={boxRef}>
          <input
            type="text"
            placeholder="Upišite grad ili naselje…"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            aria-label="Upišite grad ili naselje"
            autoComplete="off"
          />
          {open && matches.length > 0 && (
            <ul className="landing__finder-list">
              {matches.map((t, i) => (
                <li
                  key={t.slug}
                  className={i === active ? "is-active" : ""}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => { e.preventDefault(); go(t); }}
                >
                  <span className="nm">{t.name}</span>
                  <span className="c">{t.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="submit" className="landing__btn landing__btn--cta">
          Provjeri
        </button>
      </form>
      <p className="landing__finder-hint">
        ili <a href="/gradovi">pogledajte sve gradove →</a>
      </p>
    </section>
  );
}

const Landing = () => {
  const authed = useAuthed();
  const stats = usePublicStats();

  const primaryTo = authed ? "/pocetna" : "/register";
  const primaryLabel = authed ? "Moje adrese" : "Prati svoju adresu — besplatno";

  return (
    <div className="landing">
      {/* ---------------------------------------------------------------- nav */}
      <nav className="landing__nav">
        <Link to="/" className="landing__wordmark">
          <img
            className="landing__wordmark-mark"
            src={`${process.env.PUBLIC_URL}/assets/mascot/beaver-head-trim.png`}
            alt=""
            aria-hidden="true"
          />
          nemastruje<span className="landing__wordmark-dot" aria-hidden="true" />
        </Link>
        <div className="landing__nav-links">
          <a href="#kako-radi">Kako radi</a>
          <a href="#za-koga">Za koga</a>
          <a href="#pitanja">Pitanja</a>
        </div>
        <div className="landing__nav-actions">
          {!authed && (
            <Link to="/login" className="landing__btn landing__btn--ghost">
              Prijava
            </Link>
          )}
          <Link to={primaryTo} className="landing__btn landing__btn--solid">
            {authed ? "Moja ploča" : "Registriraj se"}
          </Link>
        </div>
      </nav>

      {/* --------------------------------------------------------------- hero */}
      <header className="landing__hero">
        <div className="landing__hero-glow" aria-hidden="true" />
        <div className="landing__hero-inner">
          <p className="landing__eyebrow reveal" style={{ "--d": "0ms" }}>
            Besplatne obavijesti o planiranim isključenjima struje i vode
          </p>
          <h1 className="landing__title">
            <span className="landing__title-dark reveal" style={{ "--d": "120ms" }}>
              Struja ili voda — nestat će.
            </span>
            <span className="landing__title-glow reveal" style={{ "--d": "320ms" }}>
              Ti ćeš znati prvi.
            </span>
          </h1>
          <p className="landing__sub reveal" style={{ "--d": "520ms" }}>
            HEP isključenja struje i lokalni vodovodi radove na vodi objavljuju najmanje
            48 sati unaprijed — sve zakopano u dnevnim popisima ulica. nemastruje to prati
            umjesto tebe i javi ti prije nego što ostaneš u mraku ili bez vode.
          </p>
          <div className="landing__cta-row reveal" style={{ "--d": "700ms" }}>
            <Link to={primaryTo} className="landing__btn landing__btn--cta">
              {primaryLabel}
            </Link>
            {!authed && (
              <Link to="/login" className="landing__btn landing__btn--outline">
                Imam račun
              </Link>
            )}
          </div>
          <p className="landing__micro reveal" style={{ "--d": "840ms" }}>
            Bez aplikacije · bez broja brojila · 30 sekundi
          </p>
        </div>

        <div className="landing__stats reveal" style={{ "--d": "1000ms" }}>
          <div className="landing__stat">
            <span className="landing__stat-dot" aria-hidden="true" />
            <strong>{stats ? stats.outages_upcoming : "—"}</strong>
            <span>najavljenih isključenja upravo sada</span>
          </div>
          <div className="landing__stat">
            <strong>21</strong>
            <span>distribucijsko područje — cijela Hrvatska</span>
          </div>
          <div className="landing__stat">
            <strong>4×</strong>
            <span>dnevno provjeravamo objave o struji i vodi</span>
          </div>
        </div>
      </header>

      {/* ---------------------------------------------------- how it works */}
      <main>
        <CityFinder />

        <section className="landing__section" id="kako-radi">
          <h2 className="landing__h2">
            Tri koraka do <em>mirnog mraka</em>
          </h2>
          <div className="landing__steps">
            {STEPS.map((s) => (
              <article className="landing__step" key={s.n}>
                <span className="landing__step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------ honesty */}
        <section className="landing__section landing__section--tight">
          <h2 className="landing__h2">Zar to HEP već ne radi?</h2>
          <p className="landing__lead">
            Podaci postoje — ali nitko ih ne donese do tebe. Ovako to izgleda danas:
          </p>
          <ul className="landing__compare">
            {COMPARE.map((c) => (
              <li key={c.who} className={c.us ? "is-us" : ""}>
                <strong>{c.who}</strong>
                <span>{c.how}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ------------------------------------------------------ audiences */}
        <section className="landing__section" id="za-koga">
          <h2 className="landing__h2">Za koga je nemastruje?</h2>
          <div className="landing__cards">
            {AUDIENCES.map((a) => (
              <article
                className={`landing__card${a.accent ? " landing__card--accent" : ""}`}
                key={a.title}
              >
                <span className="landing__card-tag">{a.tag}</span>
                <h3>{a.title}</h3>
                <p>{a.text}</p>
              </article>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------------ faq */}
        <section className="landing__section landing__section--tight" id="pitanja">
          <h2 className="landing__h2">Pitanja koja bi i mi postavili</h2>
          <div className="landing__faq">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* ------------------------------------------------------ final cta */}
        <section className="landing__final">
          <div className="landing__final-glow" aria-hidden="true" />
          <h2>
            Sljedeće isključenje
            <br />
            neće biti iznenađenje.
          </h2>
          <Link to={primaryTo} className="landing__btn landing__btn--cta">
            {primaryLabel}
          </Link>
          <p className="landing__micro">Besplatno za jednu adresu. Zauvijek.</p>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Landing;
